import React, { Fragment, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl, FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { principalIdSelector, foldersSelector } from 'store/selectors';
import {
  copyFromGlobalToTemplateAssets,
  getFolders,
  createFolder,
  getGlobalAssets,
  getGlobalAsset,
  updateGlobalAsset,
} from 'store/actions';
import { useDebounce } from 'use-debounce';
import { useDropzone } from 'react-dropzone';
import {
  buildGlobalIconAssetsUrl,
  buildIconAssetsUrl,
  isStringWithSpecialSigns,
  showSuccessMessage,
  showErrorMessage,
} from 'helpers';
import ConfirmModal from 'components/Modals/ConfirmModal';
import BaseModal from 'components/Modals/BaseModal';
import UploadFolderModal from 'components/Modals/UploadFolderModal';
import Button from 'components/Button';
import TextField from 'components/Forms/TextField';
import ImagesContent from 'components/Images/ImagesContent';
import ImageItem from 'components/Images/ImageItem';
import ReactTooltip from 'react-tooltip';
import cx from 'classnames';
import EditImageModal from './EditImageModal';

function GlobalIconImages({
  closeConfirmGlobalDeleteModal,
  data,
  handleDeleteGlobalImage,
  handleUploadImage,
  handleUploadWithFolder,
  handleCloseUploadFolderModal,
  id,
  isConfirmGlobalDeleteModalOpen,
  isGlobalImageDeleteLoading,
  isUploadFolderModalOpen,
  isUploadLoading,
  loading,
  onSetImageUrl,
  openConfirmGlobalDeleteModal,
  selectedImageUrl,
}) {
  const intl = useIntl();

  const dispatch = useDispatch();

  const userId = useSelector(principalIdSelector);

  const [savedNameForDelete, setSavedNameForDelete] = useState('');
  const [url, setUrl] = useState('');
  const [urlError, setUrlError] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [searchValueDebounce] = useDebounce(searchValue, 600);

  // Folder state
  const [isAddFolderModalOpen, setIsAddFolderModalOpen] = useState(false);
  const [folderName, setFolderName] = useState('');
  const [folderNameError, setFolderNameError] = useState('');
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const folders = useSelector(foldersSelector);

  const [selectedFolderIds, setSelectedFolderIds] = useState([]);
  const [isFolderMenuOpen, setIsFolderMenuOpen] = useState(false);
  const [folderSearch, setFolderSearch] = useState('');
  const folderMenuRef = useRef(null);
  const folderSearchInputRef = useRef(null);

  const selectedFolderNames = folders.data
    .filter((folder) => selectedFolderIds.includes(folder.id))
    .map((folder) => folder.name);

  const [editingAsset, setEditingAsset] = useState(null);
  const [isEditImageModalOpen, setIsEditImageModalOpen] = useState(false);
  const [isUpdatingAsset, setIsUpdatingAsset] = useState(false);
  const [isEditImageLoading, setIsEditImageLoading] = useState(false);

  useEffect(() => {
    dispatch(getFolders('icon'));
  }, [dispatch]);

  useEffect(() => {
    dispatch(getGlobalAssets({ type: 'icon', folderId: selectedFolderIds }));
  }, [dispatch, selectedFolderIds]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        folderMenuRef.current &&
        !folderMenuRef.current.contains(event.target)
      ) {
        setIsFolderMenuOpen(false);
      }
    };

    if (isFolderMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      if (folderSearchInputRef.current) {
        folderSearchInputRef.current.focus();
      }
    } else {
      setFolderSearch('');
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isFolderMenuOpen]);

  const { isDragActive, open, getRootProps, getInputProps } = useDropzone({
    onDrop: (files, rejectedFiles) => {
      if (selectedFolderIds.length > 1) {
        showErrorMessage(
          intl.formatMessage({
            id: 'image-modal.global-icons.upload-multi-folder-error',
          }),
        );
        return;
      }
      handleUploadImage(
        files,
        rejectedFiles,
        selectedFolderIds[0] || null,
        'icon',
      );
    },
    noClick: Boolean(data?.length),
    accept: 'image/jpeg, image/png, image/gif, image/svg+xml',
  });

  const handleItemClick = async (name) => {
    try {
      await dispatch(
        copyFromGlobalToTemplateAssets({ id, imageName: name, type: 'icon' }),
      );

      onSetImageUrl(buildIconAssetsUrl(id, name), name);
    } catch {
      // error toast is already shown by the thunk
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    onSetImageUrl(url);
  };

  const validateFolderName = (name) => {
    const trimmed = name.trim();

    if (!trimmed) {
      return intl.formatMessage({
        id: 'image-modal.folder-modal.name-required',
      });
    }

    if (isStringWithSpecialSigns(trimmed)) {
      return intl.formatMessage({
        id: 'image-modal.folder-modal.name-special-chars',
      });
    }

    if (trimmed.length > 255) {
      return intl.formatMessage({
        id: 'image-modal.folder-modal.name-too-long',
      });
    }

    const isDuplicate = folders?.data?.some(
      (folder) => folder.name.toLowerCase() === trimmed.toLowerCase(),
    );
    if (isDuplicate) {
      return intl.formatMessage({
        id: 'image-modal.folder-modal.name-duplicate',
      });
    }

    return '';
  };

  const handleOpenAddFolderModal = () => {
    setFolderName('');
    setFolderNameError('');
    setIsAddFolderModalOpen(true);
  };

  const handleCloseAddFolderModal = () => {
    setIsAddFolderModalOpen(false);
    setFolderName('');
    setFolderNameError('');
  };

  const handleOpenEditImageModal = async (asset) => {
    setEditingAsset(asset);
    setIsEditImageModalOpen(true);
    setIsEditImageLoading(true);

    try {
      const detailedAsset = await dispatch(
        getGlobalAsset({
          savedName: asset.savedName,
          params: { type: 'icon', folderId: asset.folderId },
        }),
      ).unwrap();

      setEditingAsset((prev) =>
        prev?.savedName === asset.savedName
          ? {
              ...prev,
              ...detailedAsset,
              thumbUrl: prev?.thumbUrl,
            }
          : prev,
      );
    } catch {
      // error toast is already shown by the thunk
    } finally {
      setIsEditImageLoading(false);
    }
  };

  const handleCloseEditImageModal = () => {
    setIsEditImageModalOpen(false);
    setEditingAsset(null);
  };

  const handleUpdateAsset = async ({ originalName, folderId }) => {
    if (!editingAsset) {
      return;
    }

    setIsUpdatingAsset(true);
    try {
      await dispatch(
        updateGlobalAsset({
          assetName: editingAsset.savedName,
          type: 'icon',
          originalName,
          folderId,
        }),
      ).unwrap();

      showSuccessMessage(
        intl.formatMessage({
          id: 'image-modal.edit-image.success-message-icon',
        }),
      );
      handleCloseEditImageModal();
      dispatch(getGlobalAssets({ type: 'icon', folderId: selectedFolderIds }));
    } catch {
      // error toast is already shown by the thunk
    } finally {
      setIsUpdatingAsset(false);
    }
  };

  const filteredFolders = folders?.data?.filter((folder) =>
    folder.name.toLowerCase().includes(folderSearch.toLowerCase()),
  );

  const handleCreateFolder = async () => {
    const error = validateFolderName(folderName);

    if (error) {
      setFolderNameError(error);
      return;
    }

    setIsCreatingFolder(true);
    try {
      const createdFolder = await dispatch(
        createFolder({ name: folderName.trim(), type: 'icon' }),
      ).unwrap();

      setSelectedFolderIds([createdFolder.id]);
      showSuccessMessage(
        intl.formatMessage({ id: 'image-modal.folder-modal.success-message' }),
      );
      handleCloseAddFolderModal();
    } catch {
      // error toast is already shown by the thunk
    } finally {
      setIsCreatingFolder(false);
    }
  };

  return (
    <div className="image-modal__content global-icons">
      <form className="image-modal__add-image-form" onSubmit={handleSubmit}>
        <fieldset disabled={loading}>
          <div className="image-modal__add-image-form-wrapper">
            <TextField
              label={intl.formatMessage({
                id: 'image-modal.global-icons.input-label',
              })}
              className="image-modal__add-image-input"
              placeholder={intl.formatMessage({
                id: 'image-modal.global-icons.input-placeholder',
              })}
              error={urlError}
              helperText={intl.formatMessage(
                urlError
                  ? {
                      id: 'image-modal.global-icons.input-validation',
                    }
                  : { id: 'image-modal.global-icons.input-description' },
              )}
              value={url}
              onChange={(e) => {
                const urlValue = e.target.value;

                if (!urlValue || urlValue.trim() === '') {
                  setUrlError(true);
                } else {
                  setUrlError(false);
                }

                setUrl(urlValue);
              }}
              fullWidth
            />
            <Button
              disabled={!url || urlError}
              type="submit"
              variant="contained"
              className="image-modal__add-image-button">
              <FormattedMessage id="image-modal.global-icons.submit-button" />
            </Button>
          </div>
        </fieldset>
      </form>
      <div className="image-modal__folder-bar" ref={folderMenuRef}>
        <Button
          variant="contained"
          className="image-modal__folder-dropdown"
          onClick={() => setIsFolderMenuOpen((prev) => !prev)}>
          <span className="image-modal__folder-dropdown__label">
            <FormattedMessage id="image-modal.global-icons.folder-label" />
          </span>
          <span
            className="image-modal__folder-dropdown__value"
            title={
              selectedFolderNames.join(', ') ||
              intl.formatMessage({ id: 'image-modal.global-icons.folder-all' })
            }>
            {selectedFolderNames.join(', ') ||
              intl.formatMessage({
                id: 'image-modal.global-icons.folder-all',
              })}
          </span>
          <span className="icon icon-expand-more" />
        </Button>
        {isFolderMenuOpen && (
          <div className="image-modal__folder-menu">
            <div className="image-modal__folder-menu-search">
              <input
                ref={folderSearchInputRef}
                type="search"
                className="image-modal__folder-menu-search__input"
                placeholder={intl.formatMessage({
                  id: 'image-modal.global-icons.folder-search-placeholder',
                })}
                value={folderSearch}
                onChange={(e) => setFolderSearch(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
            <button
              type="button"
              className={cx('image-modal__folder-menu-item', {
                'image-modal__folder-menu-item--selected':
                  selectedFolderIds.length === 0,
              })}
              onClick={() => {
                setSelectedFolderIds([]);
                setIsFolderMenuOpen(false);
              }}>
              <span className="image-modal__folder-menu-item__label">
                <FormattedMessage id="image-modal.global-icons.folder-all" />{' '}
                <span className="image-modal__folder-menu-item__default-folder">
                  <FormattedMessage id="image-modal.global-icons.default-folder" />
                </span>
              </span>
            </button>
            {filteredFolders.map((folder) => (
              <button
                key={folder.id}
                type="button"
                className={cx('image-modal__folder-menu-item', {
                  'image-modal__folder-menu-item--selected':
                    selectedFolderIds.includes(folder.id),
                })}
                onClick={() => {
                  setSelectedFolderIds((prev) => {
                    if (prev.includes(folder.id)) {
                      return prev.filter((id) => id !== folder.id);
                    }
                    return [...prev, folder.id];
                  });
                }}>
                <span className="image-modal__folder-menu-item__checkbox">
                  <input
                    type="checkbox"
                    checked={selectedFolderIds.includes(folder.id)}
                    onChange={() => {
                      setSelectedFolderIds((prev) => {
                        if (prev.includes(folder.id)) {
                          return prev.filter((id) => id !== folder.id);
                        }
                        return [...prev, folder.id];
                      });
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                </span>
                <span className="image-modal__folder-menu-item__label">
                  {folder.name}
                </span>
              </button>
            ))}
            <button
              type="button"
              className="image-modal__folder-menu-item image-modal__folder-menu-item--add-new"
              onClick={() => {
                setIsFolderMenuOpen(false);
                handleOpenAddFolderModal();
              }}>
              <FormattedMessage id="image-modal.global-icons.add-folder-button" />
            </button>
          </div>
        )}
      </div>
      <div
        {...getRootProps({
          className: cx('image-modal__images-wrapper', { isDragActive }),
        })}>
        <input {...getInputProps()} />
        <div className="image-modal__search-box">
          <input
            className="image-modal__search-box__input"
            type="search"
            placeholder={intl.formatMessage({
              id: 'image-modal.search-box.global-icons-input-placeholder',
            })}
            onChange={(e) => setSearchValue(e.target.value)}
          />
        </div>
        <ImagesContent showBigSpinner={loading}>
          {data.length ? (
            <>
              <button
                key="upload-global-icon"
                data-tip
                data-for="upload-global-icon"
                className={cx('image-modal__drop-image-button', {
                  hidden: isDragActive,
                })}
                type="button"
                onClick={open}>
                <span
                  role="img"
                  aria-label="upload to server"
                  className="icon icon-cloud-upload"
                />
              </button>
              <ReactTooltip
                id="upload-global-icon"
                type="dark"
                aria-haspopup="true"
                place="top"
                effect="solid"
                backgroundColor="#000000">
                <FormattedMessage id="image-modal.global-icons.upload-button-tooltip" />
              </ReactTooltip>
            </>
          ) : null}
          {data
            ?.filter((icon) => {
              const term = searchValueDebounce.toLowerCase();

              return (
                icon.savedName.toLowerCase().includes(term) ||
                (icon.originalName || '').toLowerCase().includes(term)
              );
            })
            ?.map((asset) => (
              <Fragment key={`global-icon-${asset.savedName}`}>
                <ImageItem
                  data-tip
                  data-for={`global-icon-${asset.savedName}`}
                  thumbUrl={buildGlobalIconAssetsUrl(userId, asset.savedName)}
                  description={asset.originalName}
                  selected={selectedImageUrl.includes(asset.savedName)}
                  onItemClick={() => handleItemClick(asset.savedName)}
                  onItemDoubleClick={() => handleItemClick(asset.savedName)}
                  onEdit={() =>
                    handleOpenEditImageModal({
                      ...asset,
                      thumbUrl: buildGlobalIconAssetsUrl(
                        userId,
                        asset.savedName,
                      ),
                    })
                  }
                  onDelete={() => {
                    setSavedNameForDelete(asset.savedName);
                    openConfirmGlobalDeleteModal();
                  }}
                />
                <ReactTooltip
                  id={`global-icon-${asset.savedName}`}
                  type="dark"
                  aria-haspopup="true"
                  place="top"
                  effect="solid"
                  backgroundColor="#000000">
                  {asset.originalName}
                </ReactTooltip>
              </Fragment>
            ))}
        </ImagesContent>
        {!loading && (
          <div
            className={cx('image-modal__drop-message', {
              visible: !data?.length,
              active: isDragActive,
            })}>
            <span
              role="img"
              aria-label="upload to server"
              className="icon icon-cloud-upload"
            />
            {data?.length ? (
              <FormattedMessage id="message.drop-to-upload" />
            ) : (
              <FormattedMessage id="message.drop-to-upload-or-click" />
            )}
          </div>
        )}
      </div>
      <BaseModal
        isOpen={isAddFolderModalOpen}
        headerTitle="image-modal.folder-modal.title"
        maxWidth="500px"
        onClose={handleCloseAddFolderModal}>
        <div className="image-modal__add-folder-form">
          <TextField
            label={intl.formatMessage({
              id: 'image-modal.folder-modal.name-label',
            })}
            placeholder={intl.formatMessage({
              id: 'image-modal.folder-modal.name-placeholder',
            })}
            error={Boolean(folderNameError)}
            helperText={folderNameError}
            value={folderName}
            onChange={(e) => {
              const value = e.target.value;

              setFolderName(value);
              setFolderNameError(validateFolderName(value));
            }}
            fullWidth
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            maxLength={255}
          />
          <div className="image-modal__add-folder-actions">
            <Button
              variant="outlined"
              onClick={handleCloseAddFolderModal}
              disabled={isCreatingFolder}>
              <FormattedMessage id="image-modal.folder-modal.cancel-button" />
            </Button>
            <Button
              variant="contained"
              onClick={handleCreateFolder}
              loading={isCreatingFolder}
              disabled={isCreatingFolder || !folderName.trim()}>
              <FormattedMessage id="image-modal.folder-modal.create-button" />
            </Button>
          </div>
        </div>
      </BaseModal>
      <BaseModal
        isOpen={isConfirmGlobalDeleteModalOpen}
        headerTitle="image-modal.global-icons.confirm-modal-header"
        maxWidth="600px"
        onClose={closeConfirmGlobalDeleteModal}>
        <ConfirmModal
          loading={isGlobalImageDeleteLoading}
          buttonsColor="secondary"
          onClose={closeConfirmGlobalDeleteModal}
          onConfirm={() => {
            handleDeleteGlobalImage(savedNameForDelete, 'icon');
            setSavedNameForDelete('');
          }}>
          <FormattedMessage id="image-modal.global-icons.confirm-modal-header.confirm-modal-description" />
        </ConfirmModal>
      </BaseModal>
      <EditImageModal
        isOpen={isEditImageModalOpen}
        asset={editingAsset}
        folders={folders.data}
        onClose={handleCloseEditImageModal}
        onSave={handleUpdateAsset}
        loading={isUpdatingAsset}
        isLoading={isEditImageLoading}
      />
      <UploadFolderModal
        isOpen={isUploadFolderModalOpen}
        isLoading={isUploadLoading}
        onClose={handleCloseUploadFolderModal}
        onUpload={handleUploadWithFolder}
        type="icon"
      />
    </div>
  );
}

GlobalIconImages.propTypes = {
  closeConfirmGlobalDeleteModal: PropTypes.func.isRequired,
  data: PropTypes.array.isRequired,
  handleDeleteGlobalImage: PropTypes.func.isRequired,
  handleUploadImage: PropTypes.func.isRequired,
  handleUploadWithFolder: PropTypes.func.isRequired,
  handleCloseUploadFolderModal: PropTypes.func.isRequired,
  id: PropTypes.string.isRequired,
  isConfirmGlobalDeleteModalOpen: PropTypes.bool.isRequired,
  isGlobalImageDeleteLoading: PropTypes.bool.isRequired,
  isUploadFolderModalOpen: PropTypes.bool.isRequired,
  isUploadLoading: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  onSetImageUrl: PropTypes.func.isRequired,
  openConfirmGlobalDeleteModal: PropTypes.func.isRequired,
  selectedImageUrl: PropTypes.string,
};

export default GlobalIconImages;
